import { AxisCode } from '@atlas/types'
import { EmptyState, ErrorState, ListSkeleton, NumberedPagination } from '@atlas/ui-components'
import { Button, Title } from '@atlas/ui-kit'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { WorkOrderTable } from '@/components/work-orders/WorkOrderTable'
import { useClassificationBootstrap } from '@/hooks/useClassification'
import { useDebouncedValue } from '@/hooks/useDebouncedValue'
import { useUserNames } from '@/hooks/useUserNames'
import { useUsers } from '@/hooks/useUsers'
import { useWorkOrderList, useWorkOrderStatuses } from '@/hooks/useWorkOrders'
import { apiErrorMessage } from '@/lib/api-error'
import { notification } from '@/notifications/notification-store'
import { brandLabelsByCode } from './brand-labels'
import { nextSort, readListState, toListParams, type WorkOrderListState, writeListState } from './list-state'
import { WorkOrderListToolbar } from './WorkOrderListToolbar'

const SEARCH_DEBOUNCE_MS = 300

/** The options each filter offers, and the brand labels the table shows. */
function useFilterOptions() {
	const { data: bootstrap } = useClassificationBootstrap()
	const { data: users = [] } = useUsers()
	const { data: statuses = [] } = useWorkOrderStatuses()
	return useMemo(() => {
		const brandValues = bootstrap?.valuesByAxis[AxisCode.BRAND] ?? []
		return {
			options: {
				brand: brandValues
					.filter((value) => value.parentId === null)
					.map((value) => ({ id: value.code, label: value.label })),
				assignee: users.map((u) => ({ id: u.userId, label: u.displayName })),
				status: statuses.map((s) => ({ id: s.code, label: s.label })),
			},
			brandLabels: brandLabelsByCode(brandValues),
		}
	}, [bootstrap, users, statuses])
}

/**
 * The search box's text, written to the URL once the user pauses typing. Only a
 * change of the debounced text writes, so back/forward to an older search is not
 * overwritten by what the box last held; such a change of the URL's `q` instead
 * resets the box to it.
 */
function useDebouncedSearch(state: WorkOrderListState, update: (patch: Partial<WorkOrderListState>) => void) {
	const [search, setSearch] = useState(state.q)
	const [seenQ, setSeenQ] = useState(state.q)
	const debounced = useDebouncedValue(search, SEARCH_DEBOUNCE_MS)
	if (state.q !== seenQ) {
		setSeenQ(state.q)
		// A `q` this hook did not write came from the URL itself.
		if (state.q !== debounced) setSearch(state.q)
	}
	const latest = useRef({ q: state.q, update })
	latest.current = { q: state.q, update }
	useEffect(() => {
		if (debounced !== latest.current.q) latest.current.update({ q: debounced })
	}, [debounced])
	return { search, setSearch }
}

interface WorkOrderListResultsProps {
	query: ReturnType<typeof useWorkOrderList>
	state: WorkOrderListState
	hasFilters: boolean
	onClearFilters: () => void
	userNames: ReadonlyMap<string, string>
	brandLabels: ReadonlyMap<string, string>
	onUpdate: (patch: Partial<WorkOrderListState>) => void
}

/** What sits under the toolbar: the failure, the first load, the empty result, or the table and its pages. */
function WorkOrderListResults({
	query,
	state,
	hasFilters,
	onClearFilters,
	userNames,
	brandLabels,
	onUpdate,
}: WorkOrderListResultsProps) {
	const navigate = useNavigate()
	const { data, isLoading, isError, isFetching, refetch } = query
	if (isError) {
		return (
			<ErrorState
				title="Couldn’t load work orders."
				action={
					<Button variant="secondary" onPress={() => void refetch()}>
						Retry
					</Button>
				}
			/>
		)
	}
	// A page past the last one is about to be replaced by the last; show it loading, not as an empty table.
	if (isLoading || !data || state.page > data.totalPages) return <ListSkeleton label="Loading work orders…" />
	if (data.total === 0) {
		return (
			<EmptyState
				variant={hasFilters ? 'filtered' : 'first-run'}
				title={hasFilters ? 'No work orders match these filters.' : 'No work orders yet.'}
				action={
					hasFilters ? (
						<Button variant="secondary" onPress={onClearFilters}>
							Clear filters
						</Button>
					) : undefined
				}
			/>
		)
	}
	return (
		<>
			<p className="mb-2 text-atlas-label text-atlas-light-dark">
				{data.total} order{data.total === 1 ? '' : 's'}
			</p>
			<WorkOrderTable
				orders={data.workOrders}
				workflows={data.workflows}
				userNames={userNames}
				brandLabels={brandLabels}
				sort={state.sort}
				order={state.order}
				onSort={(sortKey) => onUpdate(nextSort(state, sortKey))}
				onOpen={(id) => void navigate(`/work-orders/${id}`)}
			/>
			<NumberedPagination
				className="mt-ds-6"
				page={state.page}
				totalPages={data.totalPages}
				isFetching={isFetching}
				onPage={(page) => onUpdate({ page })}
			/>
		</>
	)
}

/**
 * Work-order list (route `/work-orders`): the orders as a server-sorted,
 * server-filtered, numbered-page table. Every control writes the URL, so a
 * reload or a shared link restores the view. It opens on the caller's own
 * orders. With `briefs` (route `/work-orders/briefs`) it lists the factory
 * briefs, the parent orders, instead of the orders under them.
 */
export function WorkOrderListPage({ briefs = false }: { briefs?: boolean }) {
	const [params, setParams] = useSearchParams()
	const state = readListState(params)
	const update = (patch: Partial<WorkOrderListState>, replace = false) =>
		setParams(writeListState(state, patch), { replace })
	const { search, setSearch } = useDebouncedSearch(state, update)
	const { options, brandLabels } = useFilterOptions()
	const userNames = useUserNames()
	const query = useWorkOrderList(toListParams(state, briefs))
	const { isError, error, data, isPlaceholderData } = query

	useEffect(() => {
		if (isError) notification.error(apiErrorMessage(error, 'Couldn’t load work orders.'))
	}, [isError, error])

	// A shared link, or rows leaving the result, can put the page past the end: replace it with the last page.
	const lastPage = data && !isPlaceholderData ? data.totalPages : undefined
	const clampPage = useRef(update)
	clampPage.current = update
	useEffect(() => {
		if (lastPage !== undefined && state.page > lastPage) clampPage.current({ page: lastPage }, true)
	}, [lastPage, state.page])

	const hasFilters = Boolean(state.q || state.brand.length || state.assignee.length || state.status.length)
	const clearFilters = () => {
		setSearch('')
		update({ q: '', brand: [], assignee: [], status: [] })
	}

	return (
		<div className="atlas-root flex min-w-0 flex-1 flex-col">
			<Title className="mb-6" title={briefs ? 'Factory briefs' : 'Work orders'} />
			<WorkOrderListToolbar
				scope={state.scope}
				onScope={(scope) => update({ scope })}
				search={search}
				onSearch={setSearch}
				state={state}
				options={options}
				onFilter={update}
			/>
			<WorkOrderListResults
				query={query}
				state={state}
				hasFilters={hasFilters}
				onClearFilters={clearFilters}
				userNames={userNames}
				brandLabels={brandLabels}
				onUpdate={update}
			/>
		</div>
	)
}
